import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  ListItemButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { AccountBalance, ChevronRight, Receipt } from "@mui/icons-material";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";
import type { MembershipInvoiceRow } from "../ClubMemberships/clubSummary";
import HomeSectionHeader from "./HomeSectionHeader";

type InvoiceTab = "unpaid" | "voided" | "paid";

const TAB_ORDER: InvoiceTab[] = ["unpaid", "voided", "paid"];

const isVoided = (invoice: MembershipInvoiceRow) => Boolean(invoice.voided_at);

const EMPTY_TAB_MESSAGES: Record<InvoiceTab, { title: string; body: string }> = {
  unpaid: {
    title: "No unpaid invoices",
    body: "Club invoices awaiting payment will appear here.",
  },
  voided: {
    title: "No voided invoices",
    body: "Invoices cancelled by your club will appear here.",
  },
  paid: {
    title: "No paid invoices",
    body: "Invoices you have paid will appear here.",
  },
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "—";
  const parsed = dayjs(dateString);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

const formatCurrency = (amount: number): string =>
  `£${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;

const filterInvoicesForTab = (
  tab: InvoiceTab,
  allInvoices: MembershipInvoiceRow[] | null | undefined,
): MembershipInvoiceRow[] => {
  const list = Array.isArray(allInvoices) ? allInvoices : [];
  switch (tab) {
    case "unpaid":
      return list.filter((inv) => inv && !inv.paid && !isVoided(inv));
    case "voided":
      return list.filter((inv) => inv && isVoided(inv));
    case "paid":
      return list.filter((inv) => inv && inv.paid && !isVoided(inv));
    default:
      return [];
  }
};

const pickDefaultTab = (
  unpaid: MembershipInvoiceRow[],
  voided: MembershipInvoiceRow[],
  paid: MembershipInvoiceRow[],
): InvoiceTab => {
  if (unpaid.length > 0) return "unpaid";
  if (voided.length > 0) return "voided";
  if (paid.length > 0) return "paid";
  return "unpaid";
};

interface HomeInvoicesSectionProps {
  invoices: MembershipInvoiceRow[];
  onPayClick: (invoices: MembershipInvoiceRow[]) => void;
}

interface InvoiceRowProps {
  invoice: MembershipInvoiceRow;
  showCheckbox: boolean;
  selected: boolean;
  onToggleSelect: (invoiceId: number) => void;
  onPayClick: (invoices: MembershipInvoiceRow[]) => void;
}

const InvoiceRow: React.FC<InvoiceRowProps> = ({
  invoice,
  showCheckbox,
  selected,
  onToggleSelect,
  onPayClick,
}) => {
  const voided = isVoided(invoice);
  const isOverdue =
    !invoice.paid &&
    !voided &&
    invoice.due_date &&
    dayjs(invoice.due_date).isBefore(dayjs(), "day");

  const statusLabel = voided
    ? "Voided"
    : invoice.paid
      ? "Paid"
      : isOverdue
        ? "Overdue"
        : "Unpaid";
  const statusColor = voided
    ? "default"
    : invoice.paid
      ? "success"
      : isOverdue
        ? "error"
        : "warning";

  return (
    <ListItemButton
      onClick={() => {
        if (showCheckbox && !invoice.paid && !voided) {
          onToggleSelect(invoice.id);
        } else if (!invoice.paid && !voided) {
          onPayClick([invoice]);
        }
      }}
      disabled={invoice.paid || voided}
      sx={{
        py: { xs: 2, sm: 1.75 },
        px: { xs: 2, sm: 2.5 },
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 2 },
      }}
    >
      {showCheckbox && !invoice.paid && !voided && (
        <Checkbox
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(invoice.id)}
          inputProps={{ "aria-label": `Select invoice ${invoice.id}` }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {invoice.club_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {invoice.group_name}
          {" · "}
          {formatDate(invoice.period_start)}
          {" – "}
          {formatDate(invoice.period_end)}
        </Typography>
        {!invoice.paid && !voided && (
          <Typography
            variant="caption"
            sx={{
              color: isOverdue ? "error.main" : "text.secondary",
              fontWeight: isOverdue ? 600 : 400,
            }}
          >
            {isOverdue ? "Overdue · " : "Due "}
            {formatDate(invoice.due_date)}
          </Typography>
        )}
        {voided && (
          <Typography variant="caption" color="text.secondary">
            No payment required
          </Typography>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formatCurrency(invoice.amount)}
        </Typography>
        <Chip
          label={statusLabel}
          color={statusColor}
          size="small"
          sx={{ fontWeight: 500 }}
        />
        {!invoice.paid && !voided && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountBalance />}
            onClick={(e) => {
              e.stopPropagation();
              onPayClick([invoice]);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Pay
          </Button>
        )}
      </Stack>
    </ListItemButton>
  );
};

const HomeInvoicesSection: React.FC<HomeInvoicesSectionProps> = ({
  invoices,
  onPayClick,
}) => {
  const [activeTab, setActiveTab] = useState<InvoiceTab>("unpaid");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { unpaidInvoices, voidedInvoices, paidInvoices } = useMemo(
    () => ({
      unpaidInvoices: filterInvoicesForTab("unpaid", invoices),
      voidedInvoices: filterInvoicesForTab("voided", invoices),
      paidInvoices: filterInvoicesForTab("paid", invoices),
    }),
    [invoices],
  );

  useEffect(() => {
    if (!tabInitialized && Array.isArray(invoices) && invoices.length > 0) {
      setActiveTab(
        pickDefaultTab(unpaidInvoices, voidedInvoices, paidInvoices),
      );
      setTabInitialized(true);
    }
  }, [
    tabInitialized,
    invoices,
    unpaidInvoices,
    voidedInvoices,
    paidInvoices,
  ]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const unpaidIds = new Set(unpaidInvoices.map((inv) => inv.id));
      const next = new Set<number>();
      prev.forEach((id) => {
        if (unpaidIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [unpaidInvoices]);

  const visibleInvoices = useMemo(
    () => filterInvoicesForTab(activeTab, invoices),
    [activeTab, invoices],
  );

  const activeTabIndex = TAB_ORDER.indexOf(activeTab);
  const showMultiPay = activeTab === "unpaid" && unpaidInvoices.length >= 2;

  const toggleSelect = (invoiceId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }
      return next;
    });
  };

  const selectedInvoices = unpaidInvoices.filter((inv) =>
    selectedIds.has(inv.id),
  );

  const handlePaySelected = () => {
    if (selectedInvoices.length === 0) return;
    onPayClick(selectedInvoices);
  };

  const handlePayAll = () => {
    if (unpaidInvoices.length === 0) return;
    onPayClick(unpaidInvoices);
  };

  if (!Array.isArray(invoices) || invoices.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <HomeSectionHeader
        icon={<Receipt color="primary" sx={{ fontSize: 22 }} />}
        title="Club invoices"
        subtitle={
          unpaidInvoices.length > 0
            ? `${unpaidInvoices.length} awaiting payment`
            : paidInvoices.length > 0
              ? "All invoices paid"
              : voidedInvoices.length > 0
                ? `${voidedInvoices.length} voided invoice${voidedInvoices.length === 1 ? "" : "s"}`
                : "No invoices"
        }
        action={
          <Button
            component={RouterLink}
            to="/club-memberships"
            size="small"
            endIcon={<ChevronRight />}
            sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
          >
            View in clubs
          </Button>
        }
      />

      <Tabs
        value={activeTabIndex === -1 ? 0 : activeTabIndex}
        onChange={(_, index: number) => setActiveTab(TAB_ORDER[index])}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        aria-label="Club invoice filters"
      >
        <Tab
          label={`Unpaid (${unpaidInvoices.length})`}
          sx={{ textTransform: "none", fontWeight: 600 }}
        />
        <Tab
          label={`Voided (${voidedInvoices.length})`}
          sx={{ textTransform: "none", fontWeight: 600 }}
        />
        <Tab
          label={`Paid (${paidInvoices.length})`}
          sx={{ textTransform: "none", fontWeight: 600 }}
        />
      </Tabs>

      {showMultiPay && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            startIcon={<AccountBalance />}
            disabled={selectedInvoices.length === 0}
            onClick={handlePaySelected}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Pay selected
            {selectedInvoices.length > 0 ? ` (${selectedInvoices.length})` : ""}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AccountBalance />}
            onClick={handlePayAll}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Pay all unpaid
          </Button>
        </Stack>
      )}

      {visibleInvoices.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {EMPTY_TAB_MESSAGES[activeTab].title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {EMPTY_TAB_MESSAGES[activeTab].body}
          </Typography>
        </Paper>
      ) : (
        <Paper
          key={activeTab}
          variant="outlined"
          sx={{ borderRadius: 2, overflow: "hidden" }}
        >
          {visibleInvoices.map((invoice, index) => (
            <React.Fragment key={invoice.id}>
              {index > 0 && <Divider />}
              <InvoiceRow
                invoice={invoice}
                showCheckbox={showMultiPay}
                selected={selectedIds.has(invoice.id)}
                onToggleSelect={toggleSelect}
                onPayClick={onPayClick}
              />
            </React.Fragment>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default HomeInvoicesSection;
